import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Card,
  DateTimeField,
  Field,
  Icon,
  Modal,
  ModalActions,
  SearchSelect,
  StepNumber,
  TextArea,
  Toast,
  cn,
  showToast,
  RouteMap,
  type MapPin,
  type StepState,
} from '@evogard/ui';
import {
  ASSISTANCE_SERVICES,
  dispatchAmount,
  towBreakdown,
  withAddon,
  format,
  fromColumn,
  houseLocalOf,
  instantFromHouseLocal,
  normalizePhone,
  parseMoneyInput,
  serviceScript,
  toColumn,
  type AssistancePrice,
  type AssistanceServiceType,
  type TowKind,
  SERVICE_VARIANT_FIELD,
  SERVICE_VARIANT_LABELS,
  serviceVariantsOf,
  type ServiceVariant,
} from '@evogard/domain';
import type {
  AssistanceDraft,
  AssistanceVehicle,
  DispatchDetail,
  Place,
  Quote,
  QuotedProvider,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useCopyScript } from './DispatchDesk.js';
import { AddressSearch } from './AddressSearch.js';
import { useAssistanceCatalog } from './catalog.js';
import { VehicleLookup } from './VehicleLookup.js';
import { USAGE_KEY, UsagePanel, usageRowFor, useVehicleUsage } from './UsagePanel.js';
import {
  ASSISTANCE_KEY,
  decodePolyline,
  DRAFTS_KEY,
  PAYMENT_LABEL,
  SERVICE_ICON,
  km,
  minutes,
  reais,
} from './assistance.js';
import { TowBreakdownLines } from './TowBreakdown.js';

const VEHICLE_KINDS = [
  'Automóvel',
  'Motocicleta',
  'Utilitário / SUV',
  'Caminhão',
  'Outro',
];

/** O formulário da tela. O rascunho guarda o que tem coluna (P15), e `formOfDraft` o devolve. */
interface FormState {
  plate: string;
  protocol: string;
  serviceType: AssistanceServiceType;
  /** O serviço do catálogo (02/11/2026): é dele o preço de cada prestador. */
  serviceId: number | null;
  /** O adicional (o patins, 02/11/2026): soma ao serviço, com o valor fixo do prestador. */
  addonServiceId: number | null;
  originText: string;
  origin: Place | null;
  destinationText: string;
  destination: Place | null;
  towKind: TowKind;
  /** O motivo do reboque ou o serviço residencial — escolhe a categoria do Conta Azul. */
  serviceVariant: ServiceVariant | null;
  vehicleKind: string;
  coverageKm: string;
  coverageUnlimited: boolean;
  providerId: number | null;
  informedAmount: string;
  when: 'agora' | 'agendar';
  scheduledAt: string;
  phone: string;
  description: string;
  notes: string;
}

const EMPTY: FormState = {
  plate: '',
  protocol: '',
  serviceType: 'reboque',
  serviceId: null,
  addonServiceId: null,
  originText: '',
  origin: null,
  destinationText: '',
  destination: null,
  towKind: 'comum',
  serviceVariant: null,
  vehicleKind: 'Automóvel',
  coverageKm: '',
  coverageUnlimited: false,
  providerId: null,
  informedAmount: '',
  when: 'agora',
  scheduledAt: '',
  phone: '',
  description: '',
  notes: '',
};

/**
 * O rascunho de volta no formulário. Volta o que foi escolhido — o endereço
 * confirmado, o prestador, a data —, e não o que ficou digitado pela metade.
 */
function formOfDraft(d: AssistanceDraft): FormState {
  return {
    ...EMPTY,
    plate: d.plate ?? '',
    protocol: d.opaSuiteProtocol ?? '',
    serviceType: d.serviceType,
    serviceId: d.serviceId,
    addonServiceId: d.addonServiceId,
    originText: d.origin?.label ?? '',
    origin: d.origin,
    destinationText: d.destination?.label ?? '',
    destination: d.destination,
    towKind: d.towKind ?? EMPTY.towKind,
    serviceVariant: d.serviceVariant,
    vehicleKind: d.vehicleKind ?? EMPTY.vehicleKind,
    coverageKm:
      d.planCoverageM === null ? '' : String(d.planCoverageM / 1000).replace('.', ','),
    providerId: d.providerId,
    informedAmount: d.informedAmount ? d.informedAmount.replace('.', ',') : '',
    when: d.scheduledFor ? 'agendar' : 'agora',
    scheduledAt: d.scheduledFor ? houseLocalOf(d.scheduledFor) : '',
    phone: d.callbackPhone ?? '',
    description: d.description ?? '',
    notes: d.providerNotes ?? '',
  };
}

type ClosestTo = 'veiculo' | 'destino';

/**
 * A ordem da lista de prestadores. A API devolve pela chegada ao veículo; pelo
 * destino usa a volta (`back`, do destino à base) que a cotação já trouxe —
 * trocar a ordem não chama o Google de novo. Os indisponíveis ficam no fim.
 */
function sortProviders(list: QuotedProvider[], by: ClosestTo): QuotedProvider[] {
  const key = (p: QuotedProvider) =>
    (by === 'destino' ? p.back?.m : p.approach?.s) ?? Infinity;
  return [...list].sort((a, b) => {
    if ((a.unavailable === null) !== (b.unavailable === null))
      return a.unavailable === null ? -1 : 1;
    return key(a) - key(b) || a.name.localeCompare(b.name);
  });
}

function priceOf(p: QuotedProvider): AssistancePrice | null {
  if (!p.price) return null;
  if (p.price.pricing === 'a_combinar') return { pricing: 'a_combinar' };
  const flat = p.price.flatAmount === null ? null : fromColumn(p.price.flatAmount);
  if (flat === null) return null;
  if (p.price.pricing === 'fixo') return { pricing: 'fixo', flatAmount: flat };
  const perKm = p.price.perKmAmount ? fromColumn(p.price.perKmAmount) : null;
  return perKm === null
    ? null
    : {
        pricing: 'saida_e_km',
        flatAmount: flat,
        includedM: p.price.includedM ?? 0,
        perKmAmount: perKm,
      };
}

/**
 * Novo acionamento (`assistencia-17`) — um formulário só (decidido em
 * 21/10/2026: a assistência é corrida). Desde 29/10/2026 ele abre num modal
 * largo por cima de Acionamentos, e não numa página: quem aciona continua
 * vendo a lista de onde partiu, e o rascunho continuado abre no mesmo modal.
 * Clicar fora não fecha — são dez obrigatórios; o X e o Esc fecham.
 *
 * A placa traz o associado e os produtos do veículo; o endereço vira coordenada
 * pelo Google; a cotação traz os prestadores pela chegada, com o valor de cada
 * um. A cobertura do plano é DIGITADA pela central, lendo os produtos (P3, P8):
 * o sistema não tira o km de dentro do nome do produto. A conta da tela é a
 * mesma da API (`dispatchAmount`), e a da API é a que fica gravada.
 */
export function NewDispatchModal({
  draftId,
  initialWhen = 'agora',
  onDraft,
  onClose,
}: {
  draftId?: number;
  /** "Agendar acionamento", da Agenda, abre com Agendar marcado. */
  initialWhen?: FormState['when'];
  /** A placa tem outro rascunho, e a central escolheu continuar aquele. */
  onDraft: (id: number) => void;
  onClose: () => void;
}) {
  const { data: context } = useSessionContext();
  const client = useQueryClient();
  const canCreate = can(context, 'assistance.dispatches', 'criar');
  const canDraft = canCreate;
  const [f, setF] = useState<FormState>({ ...EMPTY, when: initialWhen });
  const set = (patch: Partial<FormState>) => setF((prev) => ({ ...prev, ...patch }));
  const [vehicle, setVehicle] = useState<AssistanceVehicle | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [currentDraft, setCurrentDraft] = useState<number | null>(draftId ?? null);
  const [draftCode, setDraftCode] = useState<string | null>(null);
  const [done, setDone] = useState<DispatchDetail | null>(null);
  const [touched, setTouched] = useState(false);
  const [closestTo, setClosestTo] = useState<ClosestTo>('veiculo');
  const tow = f.serviceType === 'reboque';
  /*
   * O catálogo (02/11/2026): o tipo escolhe o grupo, e o serviço do grupo
   * escolhe o preço de cada prestador. No residencial o motivo É o serviço —
   * ele vem do serviço, e a tela não pergunta duas vezes.
   */
  const catalog = useAssistanceCatalog();
  const groupActive =
    catalog.data
      ?.find((g) => g.serviceType === f.serviceType)
      ?.services.filter((s) => s.archivedAt === null) ?? [];
  /* O adicional (o patins) não se aciona sozinho: sai da fileira e vira um "+". */
  const groupServices = groupActive.filter((s) => !s.addon);
  const groupAddons = groupActive.filter((s) => s.addon);
  const service = groupServices.find((s) => s.id === f.serviceId) ?? null;
  const addon = groupAddons.find((s) => s.id === f.addonServiceId) ?? null;
  const variants =
    f.serviceType === 'residencial' ? null : serviceVariantsOf(f.serviceType);
  /* O grupo de um serviço só já vem escolhido; o rascunho antigo, sem serviço, também. */
  useEffect(() => {
    if (f.serviceId === null && groupServices.length === 1) {
      set({ serviceId: groupServices[0]!.id });
    }
  }, [f.serviceId, f.serviceType, groupServices.length]);
  const serviceVariant: ServiceVariant | null =
    f.serviceType === 'residencial'
      ? ((service?.fixedVariant as ServiceVariant | null) ?? null)
      : f.serviceVariant;

  /*
   * A cota (01/11/2026). Quem passou da cota e tem a permissão libera com
   * motivo; quem não tem salva o rascunho e chama um líder. O servidor confere
   * de novo: esconder o botão não protege nada.
   */
  const canRelease = can(context, 'assistance.dispatches', 'liberar_cota');
  const [usageReason, setUsageReason] = useState('');
  const [continuesId, setContinuesId] = useState<number | null>(null);
  const usage = useVehicleUsage(vehicle?.vehicleId ?? null);
  const usageRow = usageRowFor(usage.data, f.serviceType, serviceVariant);
  const continuation =
    usage.data?.continuations.find((c) => c.id === continuesId) ?? null;
  const usageBlocked =
    usageRow !== null && !usageRow.canDispatch && continuation === null;
  const clearUsage = () => {
    setUsageReason('');
    setContinuesId(null);
  };

  /* Restaurar: o rascunho pedido na rota volta para o formulário. */
  const drafts = useQuery({
    queryKey: [...DRAFTS_KEY, 'abertos'],
    enabled: draftId !== undefined,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.listDrafts({ query: { discarded: 'false' } }),
        200,
        'Não foi possível carregar o rascunho.',
      ),
  });
  /* O carro escolhido, pelo `id` — a busca acha pela placa, pelo nome ou pelo CPF. */
  const lookup = useMutation({
    mutationFn: async (id: number) =>
      ok(
        await api.assistanceDispatches.vehicleByPlate({ query: { id } }),
        200,
        'Veículo não encontrado no cadastro.',
      ),
    onSuccess: (v) => {
      setVehicle(v);
      setF((prev) => ({
        ...prev,
        plate: v.plate ?? prev.plate,
        phone: prev.phone || v.memberPhone || '',
      }));
    },
    onError: () => setVehicle(null),
  });
  /* O rascunho restaurado volta com o carro que tinha. */
  useEffect(() => {
    const draft = drafts.data?.find((d) => d.id === draftId);
    if (draft) {
      setF(formOfDraft(draft));
      setCurrentDraft(draft.id);
      setDraftCode(draft.code);
      setQuote(null);
      if (draft.vehicleId !== null) lookup.mutate(draft.vehicleId);
    }
  }, [drafts.data, draftId]);

  const geocode = useMutation({
    mutationFn: async ({
      address,
    }: {
      address: string;
      target: 'origin' | 'destination';
    }) =>
      ok(
        await api.maps.geocode({ body: { address } }),
        200,
        'O Google não achou este endereço.',
      ),
    onSuccess: (place, { target }) => {
      setQuote(null);
      set(
        target === 'origin'
          ? { origin: place, originText: place.label }
          : { destination: place, destinationText: place.label },
      );
    },
  });

  const coverageM = f.coverageUnlimited
    ? null
    : f.coverageKm.trim() === ''
      ? undefined
      : Math.round(Number(f.coverageKm.replace(',', '.')) * 1000);
  const coverageValid =
    coverageM === null ||
    (coverageM !== undefined && Number.isFinite(coverageM) && coverageM >= 0);

  const quoting = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.quote({
          body: {
            serviceId: f.serviceId!,
            addonServiceId: addon?.id ?? null,
            origin: { lat: f.origin!.lat, lng: f.origin!.lng },
            destination:
              tow && f.destination
                ? { lat: f.destination.lat, lng: f.destination.lng }
                : null,
            coverageM: coverageValid && coverageM !== undefined ? coverageM : null,
          },
        }),
        200,
        'Não foi possível buscar os prestadores.',
      ),
    onSuccess: (q) => {
      setQuote(q);
      const first = sortProviders(q.providers, tow ? closestTo : 'veiculo').find(
        (p) => p.unavailable === null,
      );
      set({
        providerId:
          f.providerId && q.providers.some((p) => p.providerId === f.providerId)
            ? f.providerId
            : (first?.providerId ?? null),
      });
    },
  });

  const chosen = quote?.providers.find((p) => p.providerId === f.providerId) ?? null;
  const price = chosen ? priceOf(chosen) : null;
  /* O valor fixo do adicional na tabela do escolhido — a cotação já filtrou quem o tem. */
  const addonAmount =
    addon && chosen?.addonAmount ? fromColumn(chosen.addonAmount) : null;
  const routeView = useQuery({
    queryKey: [
      'assistencia',
      'rota',
      chosen?.providerId,
      f.origin,
      tow ? f.destination : null,
    ],
    enabled: Boolean(chosen?.depot && f.origin && (!tow || f.destination)),
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async () =>
      ok(
        await api.assistanceDispatches.routePreview({
          body: {
            providerId: chosen!.providerId,
            origin: { lat: f.origin!.lat, lng: f.origin!.lng },
            destination:
              tow && f.destination
                ? { lat: f.destination.lat, lng: f.destination.lng }
                : null,
          },
        }),
        200,
        'Não foi possível traçar a rota do prestador.',
      ),
  });
  const pins: MapPin[] = [
    ...(chosen?.depot
      ? [
          {
            ...chosen.depot,
            kind: 'depot' as const,
            label: `Base ${chosen.name}: saída e retorno`,
          },
        ]
      : []),
    ...(f.origin
      ? [
          {
            lat: f.origin.lat,
            lng: f.origin.lng,
            kind: 'vehicle' as const,
            label: 'Veículo',
          },
        ]
      : []),
    ...(tow && f.destination
      ? [
          {
            lat: f.destination.lat,
            lng: f.destination.lng,
            kind: 'destination' as const,
            label: 'Destino',
          },
        ]
      : []),
  ];
  const local = useMemo(() => {
    if (!chosen || !price || price.pricing === 'a_combinar') return null;
    if (price.pricing === 'saida_e_km' && chosen.totalM === null) return null;
    return withAddon(
      dispatchAmount(
        price,
        chosen.totalM ?? 0,
        coverageValid && coverageM !== undefined ? coverageM : null,
      ),
      addonAmount,
    );
  }, [chosen, price, coverageM, coverageValid, addonAmount]);
  /*
   * A conta do reboque aberta (02/11/2026): só com a tabela de saída + km, a
   * rota e a cobertura decidida — antes de a central digitar a cobertura, a
   * conta diria "sem limite", que não é verdade nenhuma.
   */
  const breakdown =
    price?.pricing === 'saida_e_km' &&
    chosen?.totalM != null &&
    coverageValid &&
    coverageM !== undefined
      ? towBreakdown(price, chosen.totalM, coverageM)
      : null;
  /* O a combinar nasce sem valor; a central só digita se já souber o combinado. */
  const toAgree = price?.pricing === 'a_combinar';
  /*
   * O mensal (02/11/2026): o operador não sabe o valor — o prestador repassa
   * no fim do mês, e a líder o digita no lote. Sem a tabela fechar a conta, o
   * acionamento sai sem valor, como o a combinar.
   */
  const monthlyBlank =
    chosen !== null && chosen.paymentMode === 'mensal' && local === null && !toAgree;
  const needsInformed = chosen !== null && local === null && !toAgree && !monthlyBlank;
  const informed = parseMoneyInput(f.informedAmount);
  const needsCoverage = price?.pricing === 'saida_e_km';
  const scheduledInstant =
    f.when === 'agendar' ? instantFromHouseLocal(f.scheduledAt) : null;

  /*
   * A mesma regra que o contrato aplica (`phoneSchema`). Contar só "10 dígitos
   * ou mais" deixava passar o telefone com o DDD repetido que vem do cadastro
   * do associado, e a API recusava com "Request Validation Error".
   */
  const phoneValid = normalizePhone(f.phone) !== null;

  const missing = [
    ...(vehicle ? [] : ['a placa']),
    ...(f.protocol.trim().length >= 3 ? [] : ['o protocolo OPA']),
    ...(f.origin ? [] : ['onde está o veículo']),
    ...(tow && !f.destination ? ['para onde vai'] : []),
    ...(service ? [] : ['o serviço']),
    ...(variants && f.serviceVariant === null
      ? [
          SERVICE_VARIANT_FIELD[
            f.serviceType as keyof typeof SERVICE_VARIANT_FIELD
          ].toLowerCase(),
        ]
      : []),
    ...(usageBlocked && canRelease && usageReason.trim().length < 3
      ? ['o motivo da liberação']
      : []),
    ...(chosen && chosen.unavailable === null ? [] : ['o prestador']),
    ...(needsCoverage && (coverageM === undefined || !coverageValid)
      ? ['a cobertura do plano']
      : []),
    ...(needsInformed && informed === null ? ['o valor combinado'] : []),
    ...(f.when === 'agendar' && !scheduledInstant ? ['a data do agendamento'] : []),
    ...(phoneValid ? [] : ['o WhatsApp de retorno']),
    ...(f.description.trim().length >= 3 ? [] : ['o que aconteceu']),
  ];

  /*
   * As quatro seções como passos (02/11/2026): cada uma sabe se está completa,
   * e a primeira que não está é a "atual" — é ela que acende. A conta é a
   * mesma do `missing`, repartida por seção.
   */
  const stepsDone = [
    Boolean(vehicle) &&
      f.protocol.trim().length >= 3 &&
      Boolean(service) &&
      !(variants && f.serviceVariant === null) &&
      !(usageBlocked && (!canRelease || usageReason.trim().length < 3)),
    Boolean(f.origin) && !(tow && !f.destination),
    Boolean(chosen && chosen.unavailable === null),
    (f.when !== 'agendar' || Boolean(scheduledInstant)) &&
      phoneValid &&
      f.description.trim().length >= 3,
  ];
  const currentStep = stepsDone.indexOf(false);
  const stepState = (i: number): StepState =>
    stepsDone[i] ? 'done' : i === currentStep ? 'now' : 'next';
  const stepCard = (i: number) =>
    stepState(i) === 'now' ? 'border-primaria ring-1 ring-primaria' : undefined;

  const preview = useMemo(() => {
    if (!vehicle || !chosen) return null;
    return serviceScript({
      serviceType: f.serviceType,
      serviceName: service?.name ?? null,
      towKind: tow ? f.towKind : null,
      memberName: vehicle.memberName ?? 'Associado',
      plate: vehicle.plate,
      vehicle: vehicle.label,
      consultant: null,
      cooperative: null,
      callbackPhone: f.phone || '0',
      description: f.description || '—',
      origin: f.origin?.label ?? '—',
      destination: tow ? (f.destination?.label ?? '—') : null,
      providerName: chosen.name,
      providerNotes: f.notes || null,
      scheduledFor: scheduledInstant?.toISOString() ?? null,
      linkUrl: null,
      price: local || (toAgree && informed === null) ? price : null,
      amount: local,
      coverageM: coverageValid && coverageM !== undefined ? coverageM : null,
      addon:
        addon && addonAmount !== null ? { name: addon.name, amount: addonAmount } : null,
    });
  }, [
    addon,
    addonAmount,
    vehicle,
    chosen,
    f,
    tow,
    local,
    price,
    toAgree,
    informed,
    service,
    coverageM,
    coverageValid,
    scheduledInstant,
  ]);

  const create = useMutation({
    mutationFn: async () =>
      ok(
        await api.assistanceDispatches.createDispatch({
          body: {
            serviceType: f.serviceType,
            serviceId: service!.id,
            addonServiceId: addon?.id ?? null,
            vehicleId: vehicle!.vehicleId,
            opaSuiteProtocol: f.protocol.trim(),
            origin: f.origin!,
            destination: tow ? f.destination : null,
            towKind: tow ? f.towKind : null,
            serviceVariant,
            vehicleKind: f.vehicleKind || null,
            callbackPhone: f.phone,
            description: f.description.trim(),
            providerNotes: f.notes.trim() || null,
            providerId: chosen!.providerId,
            planCoverageM: coverageValid && coverageM !== undefined ? coverageM : null,
            informedAmount:
              (needsInformed || toAgree || monthlyBlank) && informed !== null
                ? toColumn(informed)
                : null,
            scheduledFor: scheduledInstant?.toISOString() ?? null,
            draftId: currentDraft,
            usageReleaseReason: usageBlocked ? usageReason.trim() || null : null,
            continuesDispatchId: tow ? (continuation?.id ?? null) : null,
          },
        }),
        201,
        'Não foi possível acionar.',
      ),
    onSuccess: (d) => {
      setDone(d);
      void client.invalidateQueries({ queryKey: ASSISTANCE_KEY });
    },
    /* Outra pessoa pode ter acionado esta placa enquanto o formulário estava aberto. */
    onError: () => void client.invalidateQueries({ queryKey: USAGE_KEY }),
  });

  const saveDraft = useMutation({
    mutationFn: async () => {
      const body = {
        serviceType: f.serviceType,
        serviceId: service?.id ?? null,
        addonServiceId: addon?.id ?? null,
        vehicleId: vehicle?.vehicleId ?? null,
        opaSuiteProtocol: f.protocol.trim() || null,
        origin: f.origin,
        destination: tow ? f.destination : null,
        towKind: tow ? f.towKind : null,
        serviceVariant,
        vehicleKind: f.vehicleKind || null,
        callbackPhone: f.phone.trim() || null,
        description: f.description.trim() || null,
        providerNotes: f.notes.trim() || null,
        providerId: f.providerId,
        planCoverageM: coverageValid && coverageM !== undefined ? coverageM : null,
        informedAmount: informed !== null ? toColumn(informed) : null,
        scheduledFor: scheduledInstant?.toISOString() ?? null,
      };
      return currentDraft
        ? ok(
            await api.assistanceDispatches.updateDraft({
              params: { id: currentDraft },
              body,
            }),
            200,
            'Não foi possível salvar o rascunho.',
          )
        : ok(
            await api.assistanceDispatches.createDraft({ body }),
            201,
            'Não foi possível salvar o rascunho.',
          );
    },
    onSuccess: (d) => {
      setCurrentDraft(d.id);
      setDraftCode(d.code);
      void client.invalidateQueries({ queryKey: DRAFTS_KEY });
      showToast((dismiss) => (
        <Toast title={`Rascunho ${d.code} salvo`} icon="documento" onClose={dismiss}>
          <p className="text-sm">
            Fica em Acionamentos, com o status Rascunho, para toda a central.
            {d.missing.length > 0 && ` Falta: ${d.missing.join(', ')}.`}
          </p>
        </Toast>
      ));
    },
  });

  const error =
    lookup.error ?? geocode.error ?? quoting.error ?? create.error ?? saveDraft.error;

  if (done)
    return (
      <Modal
        open
        onOpenChange={(open) => !open && onClose()}
        size="lg"
        title={done.executedAt ? `${done.code} acionado` : `${done.code} agendado`}
        descricao={
          done.executedAt
            ? 'Copie o script — ele já leva o link das fotos — e mande ao prestador. Mande também a mensagem ao associado pelo OpaSuite.'
            : 'Copie o script e avise o prestador da data. O link das fotos nasce quando o agendamento for executado.'
        }
      >
        <Done
          d={done}
          onClose={() => onClose()}
          onAgain={() => {
            setDone(null);
            setF(EMPTY);
            setVehicle(null);
            setQuote(null);
            setCurrentDraft(null);
            setDraftCode(null);
            setTouched(false);
            clearUsage();
          }}
        />
      </Modal>
    );

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      size="xl"
      dismissOnOutsideClick={false}
      title={
        currentDraft
          ? `Rascunho ${draftCode ?? `nº ${currentDraft}`}`
          : 'Novo acionamento'
      }
      descricao="Colisão, roubo ou furto é sinistro: abre em Sinistros › Aviso."
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="erro">{error.message}</Alert>}

        <Card
          title={<StepTitle n={1} title="Associado e serviço" state={stepState(0)} />}
          className={stepCard(0)}
        >
          <div className="grid items-start gap-3 md:grid-cols-[minmax(0,1fr)_16rem]">
            <VehicleLookup
              text={f.plate}
              onText={(text) => set({ plate: text })}
              vehicle={vehicle}
              loading={lookup.isPending}
              error={lookup.error?.message ?? null}
              onPick={(id) => lookup.mutate(id)}
              onClear={() => {
                setVehicle(null);
                set({ plate: '' });
                clearUsage();
              }}
            />
            <Field
              label="Protocolo OPA Suite"
              required
              value={f.protocol}
              onChange={(e) => set({ protocol: e.target.value })}
            />
          </div>
          {vehicle?.draft && vehicle.draft.id !== currentDraft && (
            <Alert tone="informacao">
              Este veículo tem um rascunho, {vehicle.draft.code} (
              {ASSISTANCE_SERVICES.find((s) => s.type === vehicle.draft!.serviceType)
                ?.shortLabel ?? vehicle.draft.serviceType}
              ), de {vehicle.draft.createdByName ?? 'alguém da central'}.{' '}
              <button
                type="button"
                className="underline"
                onClick={() => onDraft(vehicle.draft!.id)}
              >
                Continuar
              </button>
            </Alert>
          )}
          {vehicle && (
            <div className="mt-4">
              <UsagePanel
                usage={usage.data}
                loading={usage.isPending}
                error={usage.error?.message ?? null}
                current={usageRow}
              />
            </div>
          )}
          {/*
           * Os tipos (29/10/2026): duas fileiras de quatro, com o ícone num
           * ladrilho e o rótulo ao lado. Oito em fila ficavam largos e baixos,
           * com um ícone pequeno no meio de um retângulo — e o escolhido só se
           * distinguia pelo fundo.
           */}
          <fieldset className="mt-4">
            <legend className="mb-2 text-sm font-medium text-texto">
              Tipo de acionamento
            </legend>
            <div
              className="grid grid-cols-2 gap-2 sm:grid-cols-4"
              role="radiogroup"
              aria-label="Tipo de acionamento"
            >
              {ASSISTANCE_SERVICES.map((s) => {
                const on = f.serviceType === s.type;
                return (
                  <button
                    key={s.type}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => {
                      set({
                        serviceType: s.type,
                        serviceId: null,
                        addonServiceId: null,
                        providerId: null,
                        serviceVariant: null,
                      });
                      setQuote(null);
                      clearUsage();
                    }}
                    className={cn(
                      'relative flex min-h-14 items-center gap-3 rounded-xl border p-2.5 text-left text-sm font-semibold transition-all',
                      on
                        ? 'border-primaria bg-primaria-suave text-texto ring-1 ring-primaria'
                        : 'border-borda bg-superficie text-texto-suave hover:border-borda-forte hover:bg-superficie-suave hover:text-texto',
                    )}
                  >
                    <span
                      className={cn(
                        'flex size-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                        on
                          ? 'bg-superficie text-texto shadow-sm'
                          : 'bg-superficie-suave text-texto-suave',
                      )}
                    >
                      <Icon nome={SERVICE_ICON[s.type]} size={20} />
                    </span>
                    <span className="min-w-0 flex-1 leading-tight">{s.shortLabel}</span>
                    {on && <Icon nome="confirmado" size={16} className="text-texto" />}
                  </button>
                );
              })}
            </div>
          </fieldset>
          {/*
           * O serviço do tipo (02/11/2026): Reboque Leve, Reboque Moto… — é dele
           * o preço de cada prestador, e só quem o atende aparece na lista. O
           * grupo de um serviço só já vem escolhido, e o campo não aparece.
           */}
          {groupServices.length > 1 && (
            <fieldset className="mt-4">
              <legend className="mb-2 text-sm font-medium text-texto">
                Serviço <span className="text-erro">*</span>
              </legend>
              <div
                className="flex flex-wrap gap-2"
                role="radiogroup"
                aria-label="Serviço"
              >
                {groupServices.map((s) => {
                  const on = f.serviceId === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => {
                        set({ serviceId: s.id, providerId: null });
                        setQuote(null);
                      }}
                      className={cn(
                        'min-h-11 rounded-xl border px-4 py-2 text-sm font-semibold transition-all',
                        on
                          ? 'border-primaria bg-primaria-suave text-texto ring-1 ring-primaria'
                          : 'border-borda bg-superficie text-texto-suave hover:border-borda-forte hover:bg-superficie-suave hover:text-texto',
                      )}
                    >
                      {tow ? s.name.replace(/^Reboque /, '') : s.name}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}
          {/*
           * O adicional (02/11/2026): "+ Patins" por cima do serviço escolhido.
           * Ligar ou desligar refaz a busca, porque só quem tem o adicional
           * fica na lista, e o valor dele soma ao do serviço.
           */}
          {groupAddons.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {groupAddons.map((a) => {
                const on = f.addonServiceId === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      set({ addonServiceId: on ? null : a.id, providerId: null });
                      setQuote(null);
                    }}
                    className={cn(
                      'inline-flex min-h-11 items-center gap-2 rounded-xl border border-dashed px-4 py-2 text-sm font-semibold transition-all',
                      on
                        ? 'border-solid border-primaria bg-primaria-suave text-texto ring-1 ring-primaria'
                        : 'border-borda-forte bg-superficie text-texto-suave hover:bg-superficie-suave hover:text-texto',
                    )}
                  >
                    <Icon nome={on ? 'confirmado' : 'mais'} size={16} />
                    {a.name}
                  </button>
                );
              })}
            </div>
          )}
          {catalog.isError && <Alert tone="erro">{catalog.error.message}</Alert>}
          {/*
           * O motivo logo abaixo do tipo (01/11/2026): a cota do reboque é do
           * motivo, e a trava precisa aparecer antes do resto do formulário.
           */}
          {variants && (
            <fieldset className="mt-4">
              <legend className="mb-2 text-sm font-medium text-texto">
                {
                  SERVICE_VARIANT_FIELD[
                    f.serviceType as keyof typeof SERVICE_VARIANT_FIELD
                  ]
                }{' '}
                <span className="text-erro">*</span>
              </legend>
              <div
                className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                role="radiogroup"
                aria-label={
                  SERVICE_VARIANT_FIELD[
                    f.serviceType as keyof typeof SERVICE_VARIANT_FIELD
                  ]
                }
              >
                {variants.map((v) => {
                  const on = f.serviceVariant === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => {
                        set({ serviceVariant: v });
                        clearUsage();
                      }}
                      className={cn(
                        'min-h-11 rounded-xl border px-3 py-2 text-sm font-semibold transition-all',
                        on
                          ? 'border-primaria bg-primaria-suave text-texto ring-1 ring-primaria'
                          : 'border-borda bg-superficie text-texto-suave hover:border-borda-forte hover:bg-superficie-suave hover:text-texto',
                      )}
                    >
                      {SERVICE_VARIANT_LABELS[v]}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}
          {usageRow?.regulationText && !usageBlocked && (
            <p className="mt-4 rounded-lg bg-superficie-suave px-3 py-2 text-sm">
              <b>No regulamento:</b> {usageRow.regulationText}
            </p>
          )}
          {continuation && (
            <div className="mt-4">
              <Alert tone="sucesso" title={`Continuação do ${continuation.code}`}>
                <p>É o segundo reboque do mesmo evento e não gasta cota.</p>
                <Button
                  className="mt-2"
                  size="sm"
                  variant="secundario"
                  onClick={() => setContinuesId(null)}
                >
                  Desfazer
                </Button>
              </Alert>
            </div>
          )}
          {usageBlocked && usageRow && (
            <div className="mt-4 flex flex-col gap-3">
              <Alert
                tone="erro"
                title={`Esta placa já usou toda a cota de ${usageRow.label.toLowerCase()}`}
              >
                A cota é {usageRow.maxUses}, e esta placa já usou {usageRow.used}
                {usageRow.usedCodes.length > 0 && ` (${usageRow.usedCodes.join(', ')})`}.
                {!canRelease &&
                  ' Você não pode liberar. Salve o rascunho e chame um líder.'}
              </Alert>
              {canRelease && (
                <Field
                  label="Motivo da liberação"
                  required
                  maxLength={500}
                  value={usageReason}
                  onChange={(e) => setUsageReason(e.target.value)}
                />
              )}
              {tow && (usage.data?.continuations.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-2">
                  {usage.data!.continuations.map((c) => (
                    <Button
                      key={c.id}
                      size="sm"
                      variant="secundario"
                      onClick={() => {
                        setContinuesId(c.id);
                        setUsageReason('');
                      }}
                    >
                      É o segundo reboque do mesmo evento ({c.code})
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="flex flex-col gap-4">
            <Card
              title={<StepTitle n={2} title="Onde e para onde" state={stepState(1)} />}
              className={stepCard(1)}
            >
              <div className="grid gap-3 md:grid-cols-2">
                <AddressSearch
                  label="Onde está o veículo"
                  value={f.originText}
                  place={f.origin}
                  onText={(text) => {
                    set({ originText: text, origin: null });
                    setQuote(null);
                  }}
                  onPlace={(place) => {
                    set({ origin: place, originText: place.label });
                    setQuote(null);
                  }}
                />
                {tow && (
                  <AddressSearch
                    label="Para onde vai"
                    value={f.destinationText}
                    place={f.destination}
                    onText={(text) => {
                      set({ destinationText: text, destination: null });
                      setQuote(null);
                    }}
                    onPlace={(place) => {
                      set({ destination: place, destinationText: place.label });
                      setQuote(null);
                    }}
                  />
                )}
                {tow && (
                  <SearchSelect
                    label="Tipo de reboque"
                    required
                    options={[
                      { value: 'comum', label: 'Comum' },
                      { value: 'emergencial', label: 'Emergencial' },
                    ]}
                    value={f.towKind}
                    onChange={(v) => set({ towKind: (v || 'comum') as TowKind })}
                  />
                )}
                <SearchSelect
                  label="Tipo de veículo"
                  options={VEHICLE_KINDS.map((v) => ({ value: v, label: v }))}
                  value={f.vehicleKind}
                  onChange={(v) => set({ vehicleKind: v })}
                />
              </div>
              {pins.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                  <RouteMap
                    path={routeView.data ? decodePolyline(routeView.data.polyline) : null}
                    pins={pins}
                    label="Mapa do itinerário"
                  />
                  {chosen && (
                    <p className="text-xs text-texto-tenue">
                      {routeView.data
                        ? `Ida ${km(routeView.data.legs[0]?.m)}${tow ? ` · reboque ${km(routeView.data.legs[1]?.m)}` : ''} · volta ${km(routeView.data.legs[routeView.data.legs.length - 1]?.m)}`
                        : routeView.isFetching
                          ? 'Buscando a rota…'
                          : (routeView.error?.message ?? '')}
                    </p>
                  )}
                </div>
              )}
            </Card>

            <Card
              title={<StepTitle n={3} title="Prestador" state={stepState(2)} />}
              className={stepCard(2)}
              actions={
                <Button
                  size="sm"
                  variant="secundario"
                  icon={quote ? 'atualizar' : 'busca'}
                  disabled={!service || !f.origin || (tow && !f.destination)}
                  loading={quoting.isPending}
                  onClick={() => quoting.mutate()}
                >
                  {quote ? 'Buscar de novo' : 'Buscar prestadores'}
                </Button>
              }
            >
              {!quote || quote.providers.length === 0 ? (
                <div className="flex items-center gap-3 rounded-lg border border-dashed border-borda-forte px-4 py-5 text-sm text-texto-tenue">
                  <Icon nome="mapa" size={20} className="shrink-0" />
                  {!quote
                    ? 'Preencha 1 e 2 e busque os prestadores.'
                    : `Nenhum prestador atende ${service?.name ?? 'este serviço'} nesta base. Marque o serviço em Plataforma › Cadastros › Prestadores › ⋯ › Assistência 24h.`}
                </div>
              ) : (
                <div
                  className="flex flex-col gap-2"
                  role="radiogroup"
                  aria-label="Prestador"
                >
                  {quote.tow && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm text-texto-suave">
                        Até o destino: {km(quote.tow.m)} · {minutes(quote.tow.s)}
                      </p>
                      <div
                        className="grid grid-cols-2 gap-1 rounded-xl bg-texto/6 p-1"
                        role="radiogroup"
                        aria-label="Ordenar prestadores"
                      >
                        {(['veiculo', 'destino'] as const).map((c) => (
                          <button
                            key={c}
                            type="button"
                            role="radio"
                            aria-checked={closestTo === c}
                            onClick={() => {
                              setClosestTo(c);
                              const first = sortProviders(quote.providers, c).find(
                                (p) => p.unavailable === null,
                              );
                              if (first) set({ providerId: first.providerId });
                            }}
                            className={cn(
                              'flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-all',
                              closestTo === c
                                ? 'bg-superficie text-texto shadow-sm'
                                : 'text-texto-suave hover:text-texto',
                            )}
                          >
                            <Icon
                              nome={c === 'veiculo' ? 'veiculo' : 'bandeira'}
                              size={16}
                            />
                            {c === 'veiculo'
                              ? 'Mais perto do veículo'
                              : 'Mais perto do destino'}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {sortProviders(quote.providers, tow ? closestTo : 'veiculo').map(
                    (p, i) => (
                      <button
                        key={p.providerId}
                        type="button"
                        role="radio"
                        aria-checked={f.providerId === p.providerId}
                        disabled={p.unavailable !== null}
                        onClick={() => set({ providerId: p.providerId })}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 rounded-xl border p-3 text-left text-sm transition-all',
                          f.providerId === p.providerId
                            ? 'border-primaria/50 bg-primaria-suave ring-1 ring-primaria/40'
                            : 'border-borda bg-superficie hover:border-borda-forte hover:bg-superficie-suave',
                          p.unavailable !== null && 'opacity-60',
                        )}
                      >
                        <span className="min-w-0">
                          <b>{p.name}</b>
                          <span className="block text-xs text-texto-suave">
                            {p.locationLabel ?? 'Sem endereço'} ·{' '}
                            {PAYMENT_LABEL[p.paymentMode]}
                          </span>
                          {p.unavailable && (
                            <span className="block text-xs text-alerta">
                              {p.unavailable}
                            </span>
                          )}
                          {i === 0 && p.unavailable === null && (
                            <Badge tone="sucesso">
                              {tow && closestTo === 'destino'
                                ? 'Mais perto do destino'
                                : 'Chega primeiro'}
                            </Badge>
                          )}
                        </span>
                        <span className="text-right tabular-nums">
                          <b>{minutes(p.approach?.s)}</b>
                          <span className="block text-xs text-texto-suave">
                            {km(p.approach?.m)} até o veículo
                          </span>
                          {tow && (
                            <span
                              className={cn(
                                'block text-xs',
                                closestTo === 'destino'
                                  ? 'font-semibold text-texto'
                                  : 'text-texto-suave',
                              )}
                            >
                              {km(p.back?.m)} do destino
                            </span>
                          )}
                        </span>
                        <span className="text-right tabular-nums">
                          <b>
                            {p.evogardAmount
                              ? reais(p.evogardAmount)
                              : p.price?.pricing === 'a_combinar'
                                ? 'A combinar'
                                : 'informar'}
                          </b>
                          <span className="block text-xs text-texto-suave">
                            {p.price?.pricing === 'a_combinar'
                              ? 'informado depois'
                              : p.totalM !== null
                                ? `${km(p.totalM)} no total`
                                : 'estimado'}
                          </span>
                        </span>
                      </button>
                    ),
                  )}
                  {quote.notServing > 0 && (
                    <p className="text-xs text-texto-tenue">
                      {quote.notServing === 1
                        ? '+1 não atende este serviço'
                        : `+${quote.notServing} não atendem este serviço`}
                    </p>
                  )}
                </div>
              )}
            </Card>

            <Card
              title={
                <StepTitle n={4} title="Quando e o que aconteceu" state={stepState(3)} />
              }
              className={stepCard(3)}
            >
              <div className="grid gap-3 md:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <div
                    className="grid grid-cols-2 gap-1 rounded-xl bg-texto/6 p-1"
                    role="radiogroup"
                    aria-label="Quando"
                  >
                    {(['agora', 'agendar'] as const).map((w) => (
                      <button
                        key={w}
                        type="button"
                        role="radio"
                        aria-checked={f.when === w}
                        onClick={() => set({ when: w })}
                        className={cn(
                          'flex h-10 items-center justify-center gap-1.5 rounded-lg text-sm font-semibold transition-all',
                          f.when === w
                            ? 'bg-superficie text-texto shadow-sm'
                            : 'text-texto-suave hover:text-texto',
                        )}
                      >
                        <Icon nome={w === 'agora' ? 'relogio' : 'calendario'} size={16} />
                        {w === 'agora' ? 'Agora' : 'Agendar'}
                      </button>
                    ))}
                  </div>
                  {f.when === 'agendar' && (
                    <DateTimeField
                      label="Data e hora"
                      required
                      value={f.scheduledAt}
                      onChange={(v) => set({ scheduledAt: v })}
                    />
                  )}
                </div>
                <Field
                  label="WhatsApp para retorno"
                  required
                  mask="phone"
                  prefix="+55"
                  value={f.phone}
                  onChange={(e) => set({ phone: e.target.value })}
                  error={
                    f.phone.trim() !== '' && !phoneValid
                      ? 'Telefone inválido: DDD e número, 10 ou 11 dígitos.'
                      : null
                  }
                />
                <TextArea
                  label="O que aconteceu"
                  required
                  value={f.description}
                  onChange={(e) => set({ description: e.target.value })}
                />
                <TextArea
                  label="Observação para o prestador"
                  value={f.notes}
                  onChange={(e) => set({ notes: e.target.value })}
                />
              </div>
            </Card>
          </div>

          <aside className="flex flex-col gap-4 lg:sticky lg:top-0">
            <Card
              title={chosen ? chosen.name : 'Resumo'}
              descricao={
                chosen?.approach
                  ? `Chega em cerca de ${minutes(chosen.approach.s)}`
                  : undefined
              }
            >
              <div className="flex flex-col gap-3 text-sm">
                {(needsCoverage || tow) && (
                  <div className="flex flex-col gap-2">
                    <Field
                      label="Cobertura do plano"
                      required={needsCoverage}
                      inputMode="decimal"
                      suffix="km"
                      disabled={f.coverageUnlimited}
                      value={f.coverageUnlimited ? '' : f.coverageKm}
                      onChange={(e) => set({ coverageKm: e.target.value })}
                      error={
                        touched &&
                        needsCoverage &&
                        (coverageM === undefined || !coverageValid)
                          ? 'Informe a cobertura, ou marque sem limite.'
                          : null
                      }
                      help={
                        vehicle?.products.length
                          ? `Produtos: ${vehicle.products.join(' · ')}`
                          : undefined
                      }
                    />
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={f.coverageUnlimited}
                        onChange={(e) => set({ coverageUnlimited: e.target.checked })}
                      />
                      Sem limite de km
                    </label>
                  </div>
                )}
                {needsInformed && (
                  <Field
                    label="Valor combinado com o prestador"
                    required
                    mask="money"
                    prefix="R$"
                    value={f.informedAmount}
                    onChange={(e) => set({ informedAmount: e.target.value })}
                  />
                )}
                {/*
                 * O a combinar (02/11/2026): o prestador atende sem preço de
                 * tabela. O acionamento sai sem valor, e ele é informado quando
                 * o prestador devolver o serviço — até lá, não entra em pagamento.
                 */}
                {(toAgree || monthlyBlank) && (
                  <div className="flex flex-col gap-2">
                    <Alert
                      tone="informacao"
                      title={monthlyBlank ? 'Prestador mensal' : 'Valor a combinar'}
                    >
                      {chosen?.paymentMode === 'mensal'
                        ? 'O valor vem do prestador e entra no fechamento do lote mensal.'
                        : 'Informado depois, em Pagamentos.'}
                    </Alert>
                    <Field
                      label="Valor combinado, se já souber"
                      mask="money"
                      prefix="R$"
                      value={f.informedAmount}
                      onChange={(e) => set({ informedAmount: e.target.value })}
                    />
                  </div>
                )}
                {local && breakdown && (
                  <div className="flex flex-col gap-2 rounded-lg border border-borda bg-superficie-suave p-3">
                    <TowBreakdownLines
                      breakdown={breakdown}
                      addon={
                        addon && addonAmount !== null
                          ? { name: addon.name, amount: addonAmount }
                          : null
                      }
                      evogardTotal={local.evogard}
                    />
                    {chosen && (
                      <div className="flex justify-between text-xs text-texto-suave">
                        <span>Pagamento</span>
                        <span>{PAYMENT_LABEL[chosen.paymentMode]}</span>
                      </div>
                    )}
                  </div>
                )}
                {local && !breakdown && (
                  <div className="flex flex-col gap-1 rounded-lg border border-borda bg-superficie-suave p-3">
                    <div className="flex justify-between">
                      <span>A Evogard paga</span>
                      <b className="tabular-nums">{format(local.evogard)}</b>
                    </div>
                    {addon && addonAmount !== null && (
                      <div className="flex justify-between text-xs text-texto-suave">
                        <span>Inclui {addon.name}</span>
                        <span className="tabular-nums">{format(addonAmount)}</span>
                      </div>
                    )}
                    {local.associate > 0 && (
                      <div className="flex justify-between">
                        <span>Acima da cobertura (associado)</span>
                        <b className="tabular-nums">{format(local.associate)}</b>
                      </div>
                    )}
                    {chosen && (
                      <div className="flex justify-between text-xs text-texto-suave">
                        <span>Pagamento</span>
                        <span>{PAYMENT_LABEL[chosen.paymentMode]}</span>
                      </div>
                    )}
                  </div>
                )}
                {preview && (
                  <details>
                    <summary className="cursor-pointer text-xs font-semibold">
                      Prévia do script
                    </summary>
                    <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-borda bg-superficie-suave p-2 font-mono text-[11px]">
                      {preview}
                    </pre>
                  </details>
                )}
                {touched && missing.length > 0 && (
                  <Alert tone="erro">Falta {missing.join(', ')}.</Alert>
                )}
                {canCreate && (
                  <Button
                    className="w-full"
                    loading={create.isPending}
                    disabled={usageBlocked && !canRelease}
                    onClick={() => {
                      setTouched(true);
                      if (missing.length === 0) create.mutate();
                    }}
                  >
                    {usageBlocked && canRelease
                      ? f.when === 'agendar'
                        ? 'Liberar e agendar acionamento'
                        : 'Liberar e acionar prestador'
                      : f.when === 'agendar'
                        ? 'Agendar acionamento'
                        : 'Acionar prestador'}
                  </Button>
                )}
                {canDraft && (
                  <Button
                    className="w-full"
                    variant="secundario"
                    icon="documento"
                    loading={saveDraft.isPending}
                    onClick={() => saveDraft.mutate()}
                  >
                    {currentDraft ? 'Atualizar rascunho' : 'Salvar rascunho'}
                  </Button>
                )}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </Modal>
  );
}

function Done({
  d,
  onClose,
  onAgain,
}: {
  d: DispatchDetail;
  onClose: () => void;
  onAgain: () => void;
}) {
  const copy = useCopyScript(d.id);
  return (
    <div className="flex flex-col gap-4">
      <Card
        title="Script do serviço"
        actions={
          <Button icon="copiar" onClick={() => void copy(d.serviceScript)}>
            Copiar script
          </Button>
        }
      >
        <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded-lg border border-borda bg-superficie-suave p-3 font-mono text-xs">
          {d.serviceScript}
        </pre>
      </Card>
      <ModalActions>
        <Button variant="secundario" onClick={onAgain}>
          Novo acionamento
        </Button>
        <Button onClick={onClose}>Fechar</Button>
      </ModalActions>
    </div>
  );
}

/**
 * O título numerado de cada seção do acionamento (02/11/2026): a orientação
 * visual que o texto de apoio fazia mal. O número diz a ordem; o `StepNumber`
 * diz se a seção está completa, se é a próxima a preencher, ou se vem depois.
 */
function StepTitle({ n, title, state }: { n: number; title: string; state: StepState }) {
  return (
    <span className="flex items-center gap-2.5">
      <StepNumber n={n} state={state} />
      <span className={cn('text-sm', state === 'next' && 'text-texto-suave')}>
        {title}
      </span>
    </span>
  );
}
